/**
 * Origin Cell Renderer for Assets Table
 *
 * Wrapper around shared OriginCell with Assets-specific features:
 * - Intelligent prioritization based on active table filters
 * - Fallback to legacy source field for backward compatibility
 * - Automatic optimization via React Compiler
 *
 * Shared features (via components/cells/OriginCell):
 * - Chariot rollup pattern for native capabilities
 * - ChipsList display with "+N" counter
 * - Tooltip showing all origins
 * - Automatic deduplication
 */

import React from 'react';

import { OriginCell as SharedOriginCell } from '@/components/table/columns/cells/OriginCell';
import { useGetVulnerabilityFilters } from '@/hooks/api/useGetVulnerabilityFilters';
import type { Asset } from '@/types';
import { getDisplayOriginTexts } from '@/utils/originDisplay.util';

import { useCellFilters } from './CellFiltersContext';
import { getPrioritizedOrigins } from './originCell.util';

interface AssetOriginCellProps {
  asset: Asset;
}

/**
 * Assets table origin cell renderer with filter-based prioritization.
 *
 * Adds intelligent prioritization on top of shared OriginCell:
 * - When filters active: Filtered origins first, then others
 * - When no filters: "Chariot" first, then others alphabetically
 *
 * React Compiler automatically optimizes this component's re-renders.
 *
 * @param asset - The asset to render origins for
 * @returns JSX element with prioritized origin chips or dash if no origins
 */
export const OriginCell: React.FC<AssetOriginCellProps> = ({ asset }) => {
  'use memo';

  // Access filter context for prioritization
  const filters = useCellFilters();
  const activeOriginFilters = filters?.advancedFilters?.origins;

  // Fetch filter options for Chariot rollup pattern
  const { data: filterOptions } = useGetVulnerabilityFilters();

  // Extract and deduplicate raw sources (with legacy source fallback)
  const rawSources = asset.origins || (asset.source ? [asset.source] : []);
  const sources = Array.from(new Set(rawSources)); // Deduplicate

  // React Query provides stable references for filterOptions.sourceFilters
  // via structural sharing and caching. Direct access is safe.
  const displaySources = getDisplayOriginTexts(
    sources,
    filterOptions?.sourceFilters || []
  );

  // Apply filter-based prioritization
  const prioritizedSources = getPrioritizedOrigins(
    displaySources,
    activeOriginFilters
  );

  // Delegate to shared component with prioritized origins
  return (
    <SharedOriginCell
      origins={sources}
      prioritizedOrigins={prioritizedSources}
      data-testid="source-chips"
    />
  );
};

OriginCell.displayName = 'OriginCell';
